/**
 * Title, meta and canonical are JSX for React 19 Float to hoist during SSR, then re-applied through the
 * DOM because Float drops them when a Suspense boundary replays. Hreflang and JSON-LD are DOM-only: Float
 * re-hoists them on every replay without deduplication, which triplicated them in the live DOM.
 */

import { truncateString } from '@worldcoo/shared/utils';
import { useEffect } from 'react';

interface HreflangAlternate {
	hreflang: string;
	href: string;
}

const localeToOgLocale: Record<string, string> = {
	es: 'es_ES',
	en: 'en_US',
	ca: 'ca_ES',
	it: 'it_IT',
	de: 'de_DE',
	fr: 'fr_FR',
	pt: 'pt_PT',
	nl: 'nl_NL',
	eu: 'eu_ES',
	gl: 'gl_ES',
};

/** Every SEO head element for a page; see the module header for which mechanism carries each. */
export const Seo = (props: {
	title?: string;
	description?: string;
	image?: string;
	imageWidth?: number;
	imageHeight?: number;
	imageType?: string;
	/** Falls back to the title. */
	imageAlt?: string;
	canonicalUrl?: string;
	locale?: string;
	alternateLocales?: string[];
	siteName?: string;
	noindex?: boolean;
	ogType?: string;
	/** Only read when `ogType` is `'article'`. */
	articleMeta?: {
		publishedTime?: string;
		tags?: string[];
	};
	alternates?: HreflangAlternate[];
	jsonLd?: Record<string, unknown>[];
}) => {
	// Google's display limit.
	const description = props.description ? truncateString(props.description, 160) : undefined;
	const ogLocale = props.locale
		? (localeToOgLocale[props.locale] ?? `${props.locale}_${props.locale.toUpperCase()}`)
		: undefined;
	const ogAlternateLocales = props.alternateLocales
		?.filter((l) => l !== props.locale)
		.map((l) => localeToOgLocale[l])
		.filter((v): v is string => Boolean(v));

	useEffect(() => {
		if (props.title) document.title = props.title;
		setMeta('name', 'description', description);
		setMeta('name', 'robots', props.noindex ? 'noindex' : undefined);
		setMeta('name', 'twitter:title', props.title);
		setMeta('name', 'twitter:description', description);
		setMeta('name', 'twitter:creator', '@worldcoo');
		setMeta('name', 'twitter:site', '@worldcoo');
		setMeta('property', 'og:type', props.ogType ?? 'website');
		setMeta('property', 'og:title', props.title);
		setMeta('property', 'og:description', description);
		setMeta('property', 'og:locale', ogLocale);
		setMeta('property', 'og:site_name', props.siteName);
		setMeta('property', 'og:url', props.canonicalUrl);
		setMeta('property', 'article:published_time', props.articleMeta?.publishedTime);
		setMeta('name', 'twitter:image', props.image);
		setMeta('name', 'twitter:image:alt', props.image ? (props.imageAlt ?? props.title) : undefined);
		setMeta('name', 'twitter:card', props.image ? 'summary_large_image' : undefined);
		setMeta('property', 'og:image', props.image);
		setMeta('property', 'og:image:width', props.image && props.imageWidth ? String(props.imageWidth) : undefined);
		setMeta('property', 'og:image:height', props.image && props.imageHeight ? String(props.imageHeight) : undefined);
		setMeta('property', 'og:image:type', props.image ? props.imageType : undefined);
		setMeta('property', 'og:image:alt', props.image ? (props.imageAlt ?? props.title) : undefined);
		syncMultiMeta('property', 'article:tag', props.articleMeta?.tags);
		syncMultiMeta('property', 'og:locale:alternate', ogAlternateLocales);

		syncHreflang(props.alternates);
		syncJsonLd(props.jsonLd);

		return () => {
			syncHreflang(undefined);
			syncJsonLd(undefined);
		};
	});

	return (
		<>
			{props.title && <title>{props.title}</title>}
			{description && <meta name="description" content={description} />}
			{props.noindex && <meta name="robots" content="noindex" />}
			{props.canonicalUrl && <link rel="canonical" href={props.canonicalUrl} />}
			<meta name="twitter:title" content={props.title} />
			{description && <meta name="twitter:description" content={description} />}
			<meta name="twitter:creator" content="@worldcoo" />
			<meta name="twitter:site" content="@worldcoo" />
			<meta property="og:type" content={props.ogType ?? 'website'} />
			{props.articleMeta?.publishedTime && (
				<meta property="article:published_time" content={props.articleMeta.publishedTime} />
			)}
			{props.articleMeta?.tags?.map((tag) => (
				<meta key={tag} property="article:tag" content={tag} />
			))}
			<meta property="og:title" content={props.title} />
			{ogLocale && <meta property="og:locale" content={ogLocale} />}
			{props.alternateLocales
				?.filter((l) => l !== props.locale)
				.map((l) => localeToOgLocale[l])
				.filter(Boolean)
				.map((ogLoc) => (
					<meta key={ogLoc} property="og:locale:alternate" content={ogLoc} />
				))}
			{props.siteName && <meta property="og:site_name" content={props.siteName} />}
			{props.canonicalUrl && <meta property="og:url" content={props.canonicalUrl} />}
			{description && <meta property="og:description" content={description} />}
			{props.image && (
				<>
					<meta name="twitter:image" content={props.image} />
					<meta name="twitter:image:alt" content={props.imageAlt ?? props.title} />
					<meta name="twitter:card" content="summary_large_image" />
					<meta property="og:image" content={props.image} />
					{!!props.imageWidth && <meta property="og:image:width" content={String(props.imageWidth)} />}
					{!!props.imageHeight && <meta property="og:image:height" content={String(props.imageHeight)} />}
					{!!props.imageType && <meta property="og:image:type" content={props.imageType} />}
					<meta property="og:image:alt" content={props.imageAlt ?? props.title} />
				</>
			)}
		</>
	);
};

/**
 * Locales without a slug fall back to `fallbackId`, which the slug-or-id lookup still resolves. There is
 * no redirect at the root, so the pathless x-default is self-canonical.
 */
export function buildAlternatesWithSlugs(
	baseUrl: string,
	locales: readonly string[],
	slugsByLocale: Record<string, string>,
	fallbackId: string,
): HreflangAlternate[] {
	const alternates: HreflangAlternate[] = locales.map((locale) => {
		const slug = slugsByLocale[locale] ?? fallbackId;
		return { hreflang: locale, href: `${baseUrl}/${locale}/${slug}` };
	});
	const defaultLocale = locales[0];
	if (defaultLocale) {
		const defaultSlug = slugsByLocale[defaultLocale] ?? fallbackId;
		alternates.push({ hreflang: 'x-default', href: `${baseUrl}/${defaultSlug}` });
	}
	return alternates;
}

/** There is no redirect at the root, so the pathless x-default is self-canonical. */
export function buildAlternates(baseUrl: string, path: string, locales: readonly string[]): HreflangAlternate[] {
	const cleanPath = path.startsWith('/') ? path : `/${path}`;
	const pathSuffix = cleanPath === '/' ? '' : cleanPath;
	const alternates: HreflangAlternate[] = locales.map((locale) => ({
		hreflang: locale,
		href: `${baseUrl}/${locale}${pathSuffix}`,
	}));
	alternates.push({ hreflang: 'x-default', href: `${baseUrl}${pathSuffix}` });
	return alternates;
}

/**
 * The canonical matches the actual URL, prefixed or not: Lighthouse flags one that matches a non-self
 * alternate. Takes `hasLocalePrefix` rather than reading context, so data-guarded callers need no conditional hook.
 */
export function resolveSlugSeo(
	hasLocalePrefix: boolean,
	baseUrl: string,
	locale: string,
	locales: readonly string[],
	slugsByLocale: Record<string, string>,
) {
	const slug = slugsByLocale[locale] ?? slugsByLocale.en!;
	return {
		slug,
		canonicalUrl: hasLocalePrefix ? `${baseUrl}/${locale}/${slug}` : `${baseUrl}/${slug}`,
		alternates: buildAlternatesWithSlugs(baseUrl, locales, slugsByLocale, slug),
	};
}

// Float's own nodes live in React's fiber tree; removing one makes a later unmount throw `removeChild` of null.
const MANAGED_ATTR = 'data-seo-managed';
const MANAGED_SELECTOR = `[${MANAGED_ATTR}="true"]`;

/**
 * Adopts Float's node rather than adding a copy: creating unconditionally put two of every tag on the www
 * home and took a replayed head from 56 children to 72. A node is created only when Float dropped its own on
 * a replay; adopting mutates, never removes (see `MANAGED_ATTR`).
 */
function setMeta(attr: 'name' | 'property', key: string, content: string | undefined) {
	const selector = `meta[${attr}="${key}"]`;
	const managed = document.head.querySelector(`${selector}${MANAGED_SELECTOR}`);
	if (!content) {
		// A Float node is React's to unmount.
		managed?.remove();
		return;
	}
	const target = managed ?? document.head.querySelector(selector);
	if (target) {
		target.setAttribute('content', content);
		return;
	}
	const el = document.createElement('meta');
	el.setAttribute(attr, key);
	el.setAttribute('content', content);
	el.setAttribute(MANAGED_ATTR, 'true');
	document.head.appendChild(el);
}

function syncMultiMeta(attr: 'name' | 'property', key: string, values: string[] | undefined) {
	for (const el of document.head.querySelectorAll(`meta[${attr}="${key}"]${MANAGED_SELECTOR}`)) el.remove();
	if (!values) return;
	// Per value, and compared in JS: free-text `article:tag` values would break a `[content="..."]` selector.
	const rendered = new Set(
		[...document.head.querySelectorAll(`meta[${attr}="${key}"]`)].map((el) => el.getAttribute('content')),
	);
	for (const value of values) {
		if (rendered.has(value)) continue;
		const el = document.createElement('meta');
		el.setAttribute(attr, key);
		el.setAttribute('content', value);
		el.setAttribute(MANAGED_ATTR, 'true');
		document.head.appendChild(el);
	}
}

function syncHreflang(alternates: HreflangAlternate[] | undefined) {
	for (const el of document.head.querySelectorAll(`link[rel="alternate"]${MANAGED_SELECTOR}`)) el.remove();
	if (!alternates) return;
	for (const alt of alternates) {
		const link = document.createElement('link');
		link.rel = 'alternate';
		link.hreflang = alt.hreflang;
		link.href = alt.href;
		link.setAttribute(MANAGED_ATTR, 'true');
		document.head.appendChild(link);
	}
}

function syncJsonLd(schemas: Record<string, unknown>[] | undefined) {
	for (const el of document.head.querySelectorAll(`script[type="application/ld+json"]${MANAGED_SELECTOR}`)) el.remove();
	if (!schemas) return;
	for (const schema of schemas) {
		const script = document.createElement('script');
		script.type = 'application/ld+json';
		script.textContent = JSON.stringify(schema);
		script.setAttribute(MANAGED_ATTR, 'true');
		document.head.appendChild(script);
	}
}
